// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Tooltip,
    makeStyles,
    shorthands,
} from '@fluentui/react-components';
import { Dismiss24Regular, Info16Regular } from '@fluentui/react-icons';
import { FluentIcon } from '@fluentui/react-icons/lib/utils/createFluentIcon';
import React from 'react';
import { ChatScope } from '../../../redux/features/conversations/ChatState';
import { Breakpoints, customTokens, useDialogClasses } from '../../../styles';
import { RootState } from '../../../redux/app/store';
import { useAppSelector } from '../../../redux/app/hooks';

const useClasses = makeStyles({
    scopeContent: {
        marginTop: customTokens.spacingVerticalL,
        marginBottom: customTokens.spacingVerticalXXL,
    },
    scopeHeader: {
        display: 'flex',
        alignItems: 'center',
        '&>h3': {
            marginLeft: customTokens.spacingHorizontalS,
            marginTop: 0,
            marginBottom: 0,
        },
    },
    infoButton: {
        ...shorthands.padding(0),
        ...shorthands.margin(0),
        minWidth: 'auto',
        marginLeft: 'auto', // align to right
        ...Breakpoints.small({
            marginLeft: 0,
        }),
    },
});

interface IScopeInfoDialogProps {
    scopeOptions: ScopeOption;
}

export interface IScopeInfo {
    name: string;
    title?: string;
    description?: string;
    icon?: FluentIcon;
    order?: number;
    subOptions?: ScopeOption;
}

export type ScopeOption = Record<number, IScopeInfo>;

export const getScopeProperty = (scopeOptions: ScopeOption, scope: ChatScope, property: string) => {
    if ((scope as number) <= Object.keys(scopeOptions).length - 1) {
        return scopeOptions[scope][property as keyof IScopeInfo];
    }

    return null;
};

export const ScopeInfoDialog: React.FC<IScopeInfoDialogProps> = ({ scopeOptions }) => {
    const { archiveSourceAvailability } = useAppSelector((state: RootState) => state.app);
    const classes = useClasses();
    const dialogClasses = useDialogClasses();

    const getDialogContent = () => {
        return Object.keys(scopeOptions).map((key) => {
            const scope = parseInt(key) as ChatScope;
            const title = getScopeProperty(scopeOptions, scope, 'title') as string;

            if (!title || (scope === ChatScope.Archive && archiveSourceAvailability && archiveSourceAvailability.Sources.length === 0)) {
                return null;
            }

            const ScopeIcon = getScopeProperty(scopeOptions, scope, 'icon') as FluentIcon;

            return (
                <div key={scope} className={classes.scopeContent}>
                    <div className={classes.scopeHeader}>
                        <ScopeIcon />
                        <h3>{title}</h3>
                    </div>
                    <p>{getScopeProperty(scopeOptions, scope, 'description') as string}</p>
                </div>
            );
        });
    };

    return (
        <Dialog>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip content={'Show source guidance'} relationship="label">
                    <Button className={classes.infoButton} icon={<Info16Regular />} appearance="transparent" />
                </Tooltip>
            </DialogTrigger>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <DialogTrigger action="close">
                                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                            </DialogTrigger>
                        }
                    >
                        Source guidance for Thomas the Al Engine
                    </DialogTitle>
                    <DialogContent>{getDialogContent()}</DialogContent>
                    <DialogActions position="start" className={dialogClasses.footer}>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary">Close</Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
